/**
 * Frontend mirror of the backend validation canon
 * (`libs/back/api/common-api/src/lib/validation/validation.constants.ts`).
 * Keep the two files in lock-step so a value the form accepts is a value the
 * API accepts (and vice-versa). Shared by ui-core validators, feature forms,
 * and the marketing site's client-side validation module.
 */

/**
 * NANP structural US phone check: any US area code (first digit 2-9), exchange
 * (first digit 2-9), 4-digit line; optional +1 / 1 prefix, parens, and `-`/`.`/
 * space separators. NOT a carrier-metadata check — new area codes are never
 * rejected.
 */
export const US_PHONE_REGEX = /^(?:\+?1[-.\s]?)?\(?([2-9]\d{2})\)?[-.\s]?([2-9]\d{2})[-.\s]?(\d{4})$/;

/** 5-digit ZIP or ZIP+4. */
export const US_ZIPCODE_REGEX = /^\d{5}(?:-\d{4})?$/;

/**
 * Anything that looks like the start of an HTML/XML tag or comment
 * (`<b`, `</script`, `<!--`, `<?xml`). Bare `<` before whitespace/digits is
 * fine — browsers do not parse `< b` as a tag.
 */
export const NO_HTML_TAG_REGEX = /<\/?[a-z!?]/i;

/**
 * Strict "no markup" check for short, single-purpose inputs (search boxes,
 * emails, codes, notify-me, promo): rejects ANY `<` or `>`, `javascript:`
 * URLs, and inline event-handler shapes (`onerror=`, ` onload =`). Stricter
 * than {@link NO_HTML_TAG_REGEX}, which deliberately lets a bare `<` through
 * for long free-text. Mirrors backend `NO_MARKUP_REGEX` / `@NoMarkup()`.
 */
export const NO_MARKUP_REGEX = /[<>]|javascript\s*:|(?:^|[^a-z0-9])on[a-z]+\s*=/i;

/** Unicode-aware allowlist for names / org names / city. */
export const SAFE_NAME_REGEX = /^[\p{L}\p{M}\p{N}\s.,'’&()#+/-]*$/u;

/** Allowlist for postal street lines. */
export const SAFE_ADDRESS_REGEX = /^[\p{L}\p{M}\p{N}\s#.,'’/-]*$/u;

/**
 * Dot-atom-only email allowlist, mirroring the backend's `SAFE_EMAIL_REGEX`
 * (#3033). A plain format check (e.g. Angular's `Validators.email`) alone
 * accepts an RFC-5321 quoted local part — `"<script>alert(1)</script>"@x.com`
 * is a structurally valid address — so a value the client accepts as "a
 * valid email" is not necessarily free of markup. This allows only unquoted
 * `atext` characters in the local part and a plain dotted hostname; pair with
 * a basic format check the same way the backend pairs `IsSafeEmail()` with
 * `@IsEmail()`.
 */
export const SAFE_EMAIL_REGEX = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

export const US_STATE_CODES: readonly string[] = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export const VALIDATION_LIMITS = {
  email: 254,
  name: 120,
  phone: 20,
  address: 120,
  city: 60,
  freeText: 5000,
} as const;

/**
 * Normalize a US phone number to `NNN-NNN-NNNN` (what `abxPhoneMask` emits and
 * what the API stores). Returns the trimmed input unchanged when it is not a
 * 10-digit (or 1+10-digit) number so validation still reports it.
 */
export function normalizeUsPhone(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return trimmed;
  let digits = trimmed.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return trimmed;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}
